import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  DocumentIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import Card from '../../../../ui/canon/Card';

const DocumentGridItem = ({
  document,
  onEdit,
  onDelete,
  onDownload,
  onView,
}) => {
  const intl = useIntl();
  const [isHovered, setIsHovered] = useState(false);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getCategoryColor = (category) => {
    const colors = {
      financial: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      legal: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      operational: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
      marketing: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
      hr: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
      other: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    };
    return colors[category] || colors.other;
  };

  return (
    <Card
      variant="outlined"
      size="md"
      className="h-full cursor-pointer relative transition-all duration-200 hover:shadow-succession-lg hover:-translate-y-1 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onView}
    >
      <div className="flex flex-col items-center text-center h-full w-full overflow-hidden">
        <div className="mb-3">
          <div className="w-16 h-16 rounded-lg bg-succession-bg-secondary flex items-center justify-center mb-3">
            <DocumentIcon className="h-8 w-8 text-succession-text-secondary" />
          </div>
          <h3 className="font-semibold text-sm line-clamp-2 min-h-[2.5rem] text-succession-text-primary">
            {document.title}
          </h3>
        </div>

        {document.category && (
          <span className={`px-2 py-0.5 text-xs rounded-md mb-2 ${getCategoryColor(document.category)}`}>
            {document.category}
          </span>
        )}

        <div className="text-xs text-succession-text-secondary text-center space-y-1 w-full max-w-full overflow-hidden px-2">
          <div className="truncate max-w-full">
            {document.file_name || document.document?.name || intl.formatMessage({ id: 'succession.vault.unknown_file' })}
          </div>
          <div>{formatFileSize(document.file_size || document.document?.file_size)}</div>
        </div>

        {document.shared_with_negotiating && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
            <ShareIcon className="h-3 w-3 text-green-600 dark:text-green-400" />
          </div>
        )}

        {isHovered && (
          <div className="absolute inset-0 bg-succession-bg-primary/95 rounded-lg flex items-center justify-center gap-2 animate-in fade-in-0 duration-200">
            <button
              onClick={(e) => { e.stopPropagation(); onView(); }}
              className="p-2 text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
              title={intl.formatMessage({ id: 'succession.vault.action_view' })}
            >
              <EyeIcon className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(); }}
              className="p-2 text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
              title={intl.formatMessage({ id: 'succession.vault.action_download' })}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="p-2 text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
              title={intl.formatMessage({ id: 'succession.vault.action_edit' })}
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            {document.can_delete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="p-2 text-red-600 dark:text-red-400 hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
                title={intl.formatMessage({ id: 'succession.vault.action_delete' })}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

DocumentGridItem.propTypes = {
  document: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

export default DocumentGridItem;
